import React from 'react'
import { Card, Flex, Tooltip, Typography } from 'antd'
import { useStore } from 'effector-react'
import { $activeBalances } from '../activeBalances/model'
import { $passiveBalances } from '../passiveBalances/model'
import { getFinsystemNumberFormat } from '../../../../../libs/utils'

const MOCK_BALANCE_STATISTICS = {
  revenue: {
    currency: 'USD',
    sum: 20000,
  },
  expenses: {
    currency: 'USD',
    sum: 20000,
  },
  profit: {
    currency: 'USD',
    sum: 20000,
  },
  margin: {
    currency: 'USD',
    sum: 20000,
  },
  activeBalance: {
    currency: 'USD',
    sum: 20000,
  },
  passiveBalance: {
    currency: 'USD',
    sum: 20000,
  },
  totalBalance: {
    currency: 'USD',
    sum: 20000,
  },
}

export const BalanceStatisticBlock: React.FC = () => {
  const activeBalances = useStore($activeBalances)
  const passiveBalances = useStore($passiveBalances)

  const revenue = activeBalances
    ? activeBalances?.turnover?.value +
      activeBalances?.lpBalance?.lpTrade +
      activeBalances?.earning
    : 0

  const expenses = activeBalances
    ? activeBalances?.liabilities.budgetPayment +
      activeBalances?.costs.commission
    : 0

  const profit = revenue - expenses

  const margin = Math.round((profit / revenue) * 100)

  return (
    <Flex gap={16} style={{ width: '100%' }}>
      <Card style={{ fontSize: '24px', width: '50%' }}>
        <Flex gap={32} wrap="wrap">
          <Tooltip placement="topLeft" title="Принос + LP trade + доходы">
            <Flex
              vertical
              wrap="nowrap"
              style={{
                flexGrow: '1',
                minWidth: '180px',
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: '18px' }}>
                Revenue
              </Typography.Text>
              <Flex gap={8}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {getFinsystemNumberFormat(revenue, 2)}
                </Typography.Title>
                <Typography.Title
                  level={3}
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  {MOCK_BALANCE_STATISTICS.revenue.currency}
                </Typography.Title>
              </Flex>
            </Flex>
          </Tooltip>

          <Tooltip placement="topLeft" title="Бюджетные платежи +комиссии">
            <Flex
              vertical
              wrap="nowrap"
              style={{
                flexGrow: '1',
                minWidth: '180px',
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: '18px' }}>
                Expenses
              </Typography.Text>
              <Flex gap={8}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {getFinsystemNumberFormat(expenses, 2)}
                </Typography.Title>
                <Typography.Title
                  level={3}
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  {MOCK_BALANCE_STATISTICS.expenses.currency}
                </Typography.Title>
              </Flex>
            </Flex>
          </Tooltip>

          <Tooltip placement="topLeft" title="Revenue-Expenses">
            <Flex
              vertical
              wrap="nowrap"
              style={{
                flexGrow: '1',
                minWidth: '180px',
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: '18px' }}>
                Profit
              </Typography.Text>
              <Flex gap={8}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {getFinsystemNumberFormat(profit, 2)}
                </Typography.Title>
                <Typography.Title
                  level={3}
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  {MOCK_BALANCE_STATISTICS.profit.currency}
                </Typography.Title>
              </Flex>
            </Flex>
          </Tooltip>

          <Tooltip placement="topLeft" title="Profit / Revenue х 100%">
            <Flex
              vertical
              wrap="nowrap"
              style={{
                flexGrow: '1',
                minWidth: '180px',
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: '18px' }}>
                Margin
              </Typography.Text>
              <Flex gap={8}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {getFinsystemNumberFormat(margin, 2)}
                </Typography.Title>
                <Typography.Title
                  level={3}
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  %
                </Typography.Title>
              </Flex>
            </Flex>
          </Tooltip>
        </Flex>
      </Card>
      <Card style={{ fontSize: '24px', width: '50%' }}>
        <Flex justify="space-between" align="top" wrap="wrap" gap={32}>
          {/* Active Balance */}
          <Tooltip placement="topLeft">
            <Flex vertical style={{ minWidth: 180, flex: 1 }}>
              <Typography.Text type="secondary" style={{ fontSize: 18 }}>
                Active balance change
              </Typography.Text>
              <Flex align="baseline" gap={8}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {getFinsystemNumberFormat(
                    activeBalances?.balanceChange ?? 0,
                    2,
                  )}
                </Typography.Title>
                <Typography.Title
                  level={3}
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  {MOCK_BALANCE_STATISTICS.activeBalance.currency}
                </Typography.Title>
              </Flex>
            </Flex>
          </Tooltip>

          {/* Passive Balance */}
          <Flex vertical style={{ minWidth: 180, flex: 1 }}>
            <Typography.Text type="secondary" style={{ fontSize: 18 }}>
              Passive balance change
            </Typography.Text>
            <Flex align="baseline" gap={8}>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {getFinsystemNumberFormat(
                  passiveBalances?.balanceChange.all ?? 0,
                  2,
                )}
              </Typography.Title>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
                type="secondary"
              >
                {MOCK_BALANCE_STATISTICS.passiveBalance.currency}
              </Typography.Title>
            </Flex>
          </Flex>

          {/* Total Change */}
          <Flex vertical style={{ minWidth: 180, flex: 1 }}>
            <Typography.Text type="secondary" style={{ fontSize: 18 }}>
              Total change
            </Typography.Text>
            <Flex align="baseline" gap={8}>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {getFinsystemNumberFormat(
                  activeBalances && passiveBalances
                    ? activeBalances?.balanceChange -
                        passiveBalances?.balanceChange.all
                    : 0,
                  2,
                )}
              </Typography.Title>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
                type="secondary"
              >
                {MOCK_BALANCE_STATISTICS.totalBalance.currency}
              </Typography.Title>
            </Flex>
          </Flex>
        </Flex>

        {/* Explanation formula */}
        <Typography.Text
          style={{ fontSize: 16, marginTop: 16, display: 'block' }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 18 }}>
            Profit{' '}
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 18 }}>
            {getFinsystemNumberFormat(profit, 2)} USD
          </Typography.Text>{' '}
          <Typography.Text type="secondary" style={{ fontSize: 18 }}>
            - Passive balance change{' '}
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 18 }}>
            {getFinsystemNumberFormat(
              passiveBalances?.balanceChange.all ?? 0,
              2,
            )}{' '}
            USD
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 18 }}>
            {' '}
            = Change{' '}
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 18 }}>
            {getFinsystemNumberFormat(
              profit - (passiveBalances?.balanceChange.all ?? 0),
              2,
            )}{' '}
            USD
          </Typography.Text>
        </Typography.Text>
      </Card>
    </Flex>
  )
}
